export type OutputFormat = 'h' | 'v' | 's';
export type Framing = 'crop' | 'fit';
export type Quality = 'orig' | '720';

export interface Geometry {
  width: number;
  height: number;
  /** direct: frames pass through untouched; scale: resize whole frame; crop: cut a region; fit: whole frame over a blurred fill. */
  mode: 'direct' | 'scale' | 'crop' | 'fit';
  /** Source region for crop mode, in source pixels. */
  crop?: { x: number; y: number; w: number; h: number };
}

const even = (n: number) => Math.max(2, Math.round(n / 2) * 2);

export function outputGeometry(
  srcW: number,
  srcH: number,
  format: OutputFormat,
  framing: Framing,
  cropPos: number,
  quality: Quality,
): Geometry {
  const pos = Math.min(1, Math.max(0, cropPos));
  if (format === 'h') {
    const targetH = quality === '720' ? Math.min(720, srcH) : srcH;
    if (targetH === srcH) return { width: even(srcW), height: even(srcH), mode: srcW % 2 || srcH % 2 ? 'scale' : 'direct' };
    return { width: even((srcW * targetH) / srcH), height: even(targetH), mode: 'scale' };
  }
  const ratio = format === 'v' ? 9 / 16 : 1;
  let width: number;
  let height: number;
  if (format === 'v') {
    height = quality === '720' ? 1280 : 1920;
    width = quality === '720' ? 720 : 1080;
  } else {
    const side = quality === '720' ? Math.min(720, srcH) : Math.min(1080, srcH);
    width = even(side);
    height = even(side);
  }
  if (framing === 'fit') return { width, height, mode: 'fit' };
  // Crop the tallest region of the target shape out of the source.
  let cw = srcH * ratio;
  let ch = srcH;
  if (cw > srcW) {
    cw = srcW;
    ch = srcW / ratio;
  }
  const x = Math.round((srcW - cw) * pos);
  const y = Math.round((srcH - ch) / 2);
  return { width, height, mode: 'crop', crop: { x, y, w: Math.round(cw), h: Math.round(ch) } };
}

/** Rough output size in bytes for the estimate on screen (H.264 at Mediabunny's high quality). */
export function estimateBytes(width: number, height: number, seconds: number): number {
  const bitsPerPixelFrame = 0.12;
  const fps = 30;
  const videoBps = Math.min(16_000_000, width * height * fps * bitsPerPixelFrame);
  const audioBps = 160_000;
  return ((videoBps + audioBps) * seconds) / 8;
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${Math.round(bytes / 1e6)} MB`;
  return `${Math.max(1, Math.round(bytes / 1e3))} KB`;
}
